import React, { useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'

import Input from '../../../../../../components/Form/Input'

import Select from '../../../../../../components/Form/Select'

import {
  ContainerPaymentWays,
  ContainerTop,
  ContainerBottom,
  Container
} from './styles'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'
import { BoxContainer, Button } from '../../../../../../components'
import {
  Form,
  FormBody,
  FormGroupContainer,
  Label
} from '../../../../../../components/Form/styles'
import {
  ProductStatus,
  ProductStatusListForSelect
} from '../../../../../../constants/product'
import ecbrApi from '../../../../../../services/ecbrApi'
import { errorResponseStatusHandler } from '../../../../../../utils/handlers/errorResponseStatusHandler'
import useUrlParam from '../../../../../../hooks/useUrlParam'
import { useQuery } from 'react-query'
import Checkbox from '../../../../../../components/Form/Checkbox'
import { maskToCurrency } from '../../../../../../utils/maskFunctions/maskToCurrency'
import { currencyToNumber } from '../../../../../../utils/currencyToNumber'
import { numberToCurrency } from '../../../../../../utils/numberToCurrency'
import { centsToReal } from '../../../../../../helpers/centsToReal'
import { realToCents } from '../../../../../../helpers/realToCents'

const initialValues = {
  code: '',
  name: '',
  amount: '',
  maxInstallments: '',
  description: '',
  status: ProductStatus.AVAILABLE,
  isBankSlip: false,
  isCreditCard: false,
  isDebitCard: false,
  isPix: false
}

const formSchema = Yup.object().shape({
  code: Yup.string().required('Campo obrigatório'),
  name: Yup.string().required('Campo obrigatório'),
  amount: Yup.number()
    .required('Campo obrigatório')
    .test('is-currency-real', 'Valor inválido', (_, { originalValue }) => {
      // Using Regex to validate currency real. Ex: R$ 1.112,22
      const regex = /^(R\$\s?)?(([1-9]\d{0,2}(\.\d{3})*)|0)?(,\d{1,2})?$/
      return regex.test(originalValue)
    })
    .transform((_, value) => currencyToNumber(value))
    .min(0.01, 'Valor inválido'),
  maxInstallments: Yup.number()
    .transform((_, obj) => obj?.value ?? undefined)
    .required('Campo obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  status: Yup.string()
    .transform((_, obj) => obj?.value ?? undefined)
    .required('Campo obrigatório'),
  isBankSlip: Yup.boolean().required('Campo obrigatório'),
  isCreditCard: Yup.boolean().required('Campo obrigatório'),
  isDebitCard: Yup.boolean().required('Campo obrigatório'),
  isPix: Yup.boolean().required('Campo obrigatório')
})

const installmentsListForSelect = [...Array(12).entries()].map((_, index) => ({
  value: index + 1,
  label: `${index + 1}x`
}))

const PriceAndConditions = () => {
  const [error, setError] = useState('')
  const [saveBtnText, setSaveBtnText] = useState('Salvar')
  const [productId] = useUrlParam('/admin/certificates/:id')

  const {
    data: certificate,
    isLoading,
    isSuccess,
    isError,
    error: _error,
    refetch
  } = useQuery('certificate', async () => ecbrApi.products.getById(productId), {
    enabled: !!productId,
    select: ({ data }) => {
      const { amount, status, maxInstallments, ...rest } = data
      return {
        ...rest,
        amount: numberToCurrency(centsToReal(amount)),
        status: ProductStatusListForSelect.find(
          ({ value }) => value === status
        ),
        maxInstallments: installmentsListForSelect.find(
          ({ value }) => value === maxInstallments
        )
      }
    },
    onSuccess: () => {
      setError('')
    },
    onError: () => {
      setError('Não foi possível carregar os dados')
    },
    cacheTime: 0
  })

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true)
      const { amount, status, maxInstallments, ...restValues } = values

      const _certificate = {
        ...restValues,
        amount: realToCents(currencyToNumber(amount)),
        status: status.value,
        maxInstallments: maxInstallments.value
      }

      await ecbrApi.products.create(_certificate)
      await refetch()

      setSaveBtnText('Salvo!')
      setTimeout(() => {
        setSaveBtnText('Salvar')
        setSubmitting(false)
      }, 1000)
    } catch (err) {
      errorResponseStatusHandler(err, setError)
      setSubmitting(false)
    }
  }

  return (
    <span>
      <BoxContainer>
        {isLoading && <p>Carregando...</p>}
        {isError && <p>Ocorreu um erro ao carregar os dados</p>}
        {isSuccess && (
          <Formik
            initialValues={certificate ?? initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form maxWidth="100%" margin="0">
                <FormBody type="flex" justifyContent="space-between" flexRow>
                  <Container>
                    <ContainerTop>
                      <Input
                        type="text"
                        name="code"
                        label="Código"
                        placeholder="Código do certificado"
                        display="inline-block"
                        width="140px"
                        fontSizeLabel="12px"
                        disabled
                      />
                      <Select
                        name="status"
                        label="Status"
                        options={ProductStatusListForSelect}
                        optionValue="value"
                        optionLabel="label"
                        width="180px"
                        fontSizeLabel="12px"
                      />
                      <Input
                        type="text"
                        name="name"
                        label="Nome"
                        placeholder="Nome do certificado"
                        fontSizeLabel="12px"
                        flexGrow
                      />
                      <Input
                        type="text"
                        name="description"
                        label="Descrição"
                        placeholder="Descrição"
                        width="220px"
                        fontSizeLabel="12px"
                      />
                      <Input
                        type="text"
                        name="amount"
                        label="Valor"
                        placeholder="Valor do certificado"
                        mask="R$ 9999999999"
                        alwaysShowMask={false}
                        beforeMaskedStateChange={maskToCurrency}
                        display="inline-block"
                        width="180px"
                        fontSizeLabel="12px"
                      />
                      <Select
                        name="maxInstallments"
                        label="Parcelamento máximo"
                        options={installmentsListForSelect}
                        optionValue="value"
                        optionLabel="label"
                        width="170px"
                        fontSizeLabel="12px"
                      />
                      <ContainerPaymentWays>
                        <Label fontSizeLabel="12px">Formas de pagamento</Label>
                        <FormGroupContainer margin="0.6rem 0">
                          <Checkbox
                            name="isCreditCard"
                            label="Cartão de crédito"
                          />
                          {/* <Checkbox
                            name="isDebitCard"
                            label="Cartão de débito"
                          /> */}
                          <Checkbox name="isBankSlip" label="Boleto bancário" />
                          <Checkbox name="isPix" label="Pix" />
                        </FormGroupContainer>
                      </ContainerPaymentWays>
                    </ContainerTop>
                    <ContainerBottom>
                      <ErrorMessage>{error}</ErrorMessage>
                      <Button
                        text={saveBtnText}
                        type="submit"
                        variant="primary"
                        width="130px"
                        disabled={isSubmitting}
                      />
                    </ContainerBottom>
                  </Container>
                </FormBody>
              </Form>
            )}
          </Formik>
        )}
      </BoxContainer>
    </span>
  )
}

export default PriceAndConditions
